import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, Instagram, Linkedin, Twitter } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/SiteContext";

const PAGES = [
  { to: "/", label: "Home" }, { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" }, { to: "/blog", label: "Blog" },
  { to: "/pricing", label: "Pricing" }, { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" }, { to: "/faq", label: "FAQ" },
];

const SERVICES = [
  "Technical SEO", "On-Page SEO", "Content SEO", "Local SEO",
  "E-Commerce SEO", "Link Building", "SEO Audits", "WordPress & Shopify SEO",
];

export function Footer() {
  const site = useSite();
  return (
    <footer className="border-t border-rw-border bg-rw-bg2" data-testid="footer">
      <div className="container-rw py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-rw-muted leading-relaxed max-w-xs">
            {site.tagline} We help businesses turn Google searches into measurable business opportunities —
            and build the fast, search-friendly websites behind them.
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-rw-green">SEO First. Always.</p>
          {(site.socials?.instagram || site.socials?.linkedin || site.socials?.twitter) && (
            <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
              {site.socials.instagram && (
                <a href={site.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Ravi Works on Instagram"
                  data-testid="footer-instagram"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-rw-border text-rw-muted transition-all hover:border-rw-green/60 hover:text-rw-green">
                  <Instagram size={16} />
                </a>
              )}
              {site.socials.linkedin && (
                <a href={site.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Ravi Works on LinkedIn"
                  data-testid="footer-linkedin"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-rw-border text-rw-muted transition-all hover:border-rw-green/60 hover:text-rw-green">
                  <Linkedin size={16} />
                </a>
              )}
              {site.socials.twitter && (
                <a href={site.socials.twitter} target="_blank" rel="noopener noreferrer" aria-label="Ravi Works on X"
                  data-testid="footer-twitter"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-rw-border text-rw-muted transition-all hover:border-rw-green/60 hover:text-rw-green">
                  <Twitter size={16} />
                </a>
              )}
            </div>
          )}
        </div>

        <nav aria-label="Footer pages">
          <h3 className="font-jakarta font-bold text-sm uppercase tracking-wider text-rw-text mb-4">Pages</h3>
          <ul className="space-y-2.5">
            {PAGES.map((p) => (
              <li key={p.to}>
                <Link to={p.to} data-testid={`footer-link-${p.label.toLowerCase()}`}
                  className="text-sm text-rw-muted hover:text-rw-green transition-colors">{p.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="font-jakarta font-bold text-sm uppercase tracking-wider text-rw-text mb-4">SEO Services</h3>
          <ul className="space-y-2.5">
            {SERVICES.map((s) => (
              <li key={s}>
                <Link to="/services" data-testid={`footer-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-sm text-rw-muted hover:text-rw-green transition-colors">{s}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-jakarta font-bold text-sm uppercase tracking-wider text-rw-text mb-4">Contact</h3>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={site.mailLink} data-testid="footer-email" className="flex items-center gap-2.5 text-rw-muted hover:text-rw-green transition-colors">
                <Mail size={16} className="text-rw-green shrink-0" /> {site.email}
              </a>
            </li>
            <li>
              <a href={site.telLink} data-testid="footer-phone" className="flex items-center gap-2.5 text-rw-muted hover:text-rw-green transition-colors">
                <Phone size={16} className="text-rw-green shrink-0" /> {site.phone_display}
              </a>
            </li>
            <li>
              <a href={site.waLink} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
                className="flex items-center gap-2.5 text-rw-muted hover:text-rw-green transition-colors">
                <MessageCircle size={16} className="text-rw-green shrink-0" /> WhatsApp: {site.phone_display}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-rw-border">
        <div className="container-rw py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-rw-dim">© 2026 Ravi Works. All rights reserved.</p>
          <p className="text-xs text-rw-dim font-mono">Technical SEO · Content SEO · Local SEO · E-Commerce SEO · SMO</p>
        </div>
      </div>
    </footer>
  );
}
